import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { isoDay } from '../utils/format.js';

/**
 * A month grid, shared by the booking screen and the team agenda.
 *
 * It only draws the calendar: what goes inside a day is entirely up to the
 * caller via `renderDay(dateKey, date)`. That keeps one grid implementation
 * for "who is available" and "what is booked".
 */

/** Month cells starting on Monday, padded with nulls at both ends. */
export const buildMonthGrid = (year, month) => {
  const first = new Date(Date.UTC(year, month, 1));
  const daysInMonth = new Date(Date.UTC(year, month + 1, 0)).getUTCDate();
  const isoWeekday = first.getUTCDay() === 0 ? 7 : first.getUTCDay();

  const cells = Array.from({ length: isoWeekday - 1 }, () => null);
  for (let day = 1; day <= daysInMonth; day += 1) cells.push(new Date(Date.UTC(year, month, day)));
  while (cells.length % 7 !== 0) cells.push(null);
  return cells;
};

export const firstOfMonth = (date = new Date()) =>
  new Date(Date.UTC(date.getUTCFullYear(), date.getUTCMonth(), 1));

/** Prev / label / next / today. */
export const MonthNav = ({ cursor, onChange }) => {
  const { t, i18n } = useTranslation();

  const label = new Intl.DateTimeFormat(i18n.resolvedLanguage, {
    month: 'long',
    year: 'numeric',
    timeZone: 'UTC',
  }).format(cursor);

  const shift = (delta) =>
    onChange(new Date(Date.UTC(cursor.getUTCFullYear(), cursor.getUTCMonth() + delta, 1)));

  return (
    <div className="month-nav">
      <button type="button" className="btn btn--small" onClick={() => shift(-1)} aria-label={t('common.previous')}>
        &lsaquo;
      </button>
      <span className="month-nav__label">{label}</span>
      <button type="button" className="btn btn--small" onClick={() => shift(1)} aria-label={t('common.next')}>
        &rsaquo;
      </button>
      <button type="button" className="btn btn--small" onClick={() => onChange(firstOfMonth())}>
        {t('common.today')}
      </button>
    </div>
  );
};

/**
 * `onDayClick` makes the whole day cell the target instead of whatever sits
 * inside it. The booking screen wants that - you pick a day, then a time - while
 * the team agenda leaves it off so each RDV chip stays individually clickable.
 */
const MonthCalendar = ({ cursor, renderDay, onDayClick }) => {
  const { t, i18n } = useTranslation();
  const year = cursor.getUTCFullYear();
  const month = cursor.getUTCMonth();

  // On a phone the 7-column grid collapses to a day list, where a bare "14"
  // means nothing - so every cell also carries a full date label, hidden on
  // wide screens by CSS.
  const dayLabel = new Intl.DateTimeFormat(i18n.resolvedLanguage, {
    weekday: 'long',
    day: 'numeric',
    month: 'long',
    timeZone: 'UTC',
  });

  const cells = useMemo(() => buildMonthGrid(year, month), [year, month]);
  const todayKey = isoDay(new Date());

  return (
    <div className="calendar">
      <div className="calendar__head">
        {[1, 2, 3, 4, 5, 6, 7].map((day) => (
          <span key={day} className="calendar__weekday">
            {t(`agenda.weekdaysShort.${day}`)}
          </span>
        ))}
      </div>

      <div className="calendar__grid">
        {cells.map((date, index) => {
          if (!date) return <div key={`blank-${index}`} className="calendar__cell calendar__cell--empty" />;

          const key = isoDay(date);
          const content = renderDay(key, date);

          const className = [
            'calendar__cell',
            key === todayKey ? 'calendar__cell--today' : '',
            key < todayKey ? 'calendar__cell--past' : '',
            content ? 'calendar__cell--has-slots' : '',
            onDayClick && content ? 'calendar__cell--clickable' : '',
          ]
            .filter(Boolean)
            .join(' ');

          const inner = (
            <>
              <span className="calendar__daynum">{date.getUTCDate()}</span>
              <span className="calendar__daylabel">{dayLabel.format(date)}</span>
              <div className="calendar__people">{content}</div>
            </>
          );

          // A real button when the day is the target, so it is keyboard-reachable.
          if (onDayClick && content) {
            return (
              <button type="button" key={key} className={className} onClick={() => onDayClick(key, date)}>
                {inner}
              </button>
            );
          }

          return (
            <div key={key} className={className}>
              {inner}
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default MonthCalendar;
